import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { AppUtils } from '../../../core/config/app.utils';
import { LoadingService } from '../../../core/services/loading.service';
import { UserService } from '../../../core/services/user.service';
import { ActionAlert } from '../../../core/dto/actionAlert';
import { AuthService } from '../../../modules/auth/services/auth.service';
import { AdminForgotPasswordService } from '../../../modules/auth/services/admin-forgot-password.service';
import { SETTINGS } from '../../../core/config/common.settings';
import { FileService } from '../../../core/services/file.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { CountryService } from '../../../core/services/country.service';
import { LoggedInUserService } from '../../../core/services/loggedInUser.service';

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
})
export class AccountComponent implements OnInit {
  accountForm: any;
  accountFormErrors: any;
  accountInformation: any;
  actionMessage = new ActionAlert();
  userId: any;
  user: any;
  msg: any;

  // Password change
  passwordChangeForm: any;
  passwordChangeFormErrors: any;
  confirmPasswordVisible = true;
  newPasswordVisible = true;
  currentPasswordVisible = true;

  uploadAndPreview = true;
  profilePlaceholderPath: any = '../assets/img/profile_avatar.jpg';
  files: File[] = [];
  countries: any[] = [];

  constructor(
    private fb: FormBuilder,
    private loading: LoadingService,
    private userService: UserService,
    private authService: AuthService,
    private adminForgotPasswordService: AdminForgotPasswordService,
    private fileService: FileService,
    private message: NzMessageService,
    private countryService: CountryService,
    private loggedInUserService: LoggedInUserService,
    private translocoService: TranslocoService
  ) {
    this.accountFormErrors = {
      firstname: {},
      lastname: {},
      email: {},
      mobile: {},
      dialCode: {},
    };

    // Password change
    this.passwordChangeFormErrors = {
      currentPassword: {},
      newPassword: {},
      confirmPassword: {},
    };
  }

  ngOnInit(): void {
    this.findUserId();
    this.getCountries();
    this.accountForm = this.fb.group({
      firstname: ['', [Validators.required]],
      lastname: [''],
      email: [
        { value: '', disabled: true },
        [Validators.required, Validators.email],
      ],
      mobile: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(SETTINGS.REGEX.PHONE.EXP),
        ]),
      ],
      dialCode: ['', [Validators.required]],
      profilePic: [''],
    });

    this.accountForm.valueChanges.subscribe(() => {
      AppUtils.getFormErrors(this.accountForm, this.accountFormErrors);
    });

    // Password change
    this.passwordChangeForm = this.fb.group({
      currentPassword: [null, [Validators.required]],
      newPassword: [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(SETTINGS.REGEX.PASSWORD.EXP),
        ]),
      ],
      confirmPassword: [null, [this.confirmationValidator]],
    });

    this.passwordChangeForm.valueChanges.subscribe(() => {
      AppUtils.getFormErrors(
        this.passwordChangeForm,
        this.passwordChangeFormErrors
      );
    });
    this.passwordChangeForm.get('newPassword').valueChanges.subscribe(() => {
      this.passwordChangeForm.get('confirmPassword').reset();
    });
  }

  async findUserId(): Promise<any> {
    this.loading.start();
    this.user = await this.authService.getLoggedInUser();
    this.userId = this.user.userId;
    await this.findAccount();
    this.loading.stop();
  }

  async findAccount(): Promise<any> {
    try {
      const payload = this.userId;
      this.accountInformation = await this.userService.getUserByID(payload);
      this.accountForm.patchValue(this.accountInformation);
    } catch (e) {}
  }

  onSelect(event: any): void {
    this.loading.start();
    this.files.push(...event.addedFiles);
    this.fileService.postFile(this.files[0]).subscribe(res => {
      this.accountForm.get('profilePic').setValue(res.url);
      this.uploadAndPreview = true;
      this.loading.stop();
    });
  }
  onRemove(event: any): void {
    this.files.splice(this.files.indexOf(event), 1);
    this.accountForm.get('profilePic').setValue('');
  }

  async updateAccount(type: any): Promise<any> {
    this.loading.start();
    try {
      const payload = {
        _id: this.userId,
        ...this.accountForm.getRawValue(),
      };
      const data = await this.userService.editUser(payload);
      await this.userService.getUserByIDToHeader(data._id);
      this.msg = type;
      this.actionMessage = new ActionAlert(
        true,
        true,
        this.translocoService.translate(
          'settings.account.messages.accountInformationUpdated'
        ),
        'success'
      );
      this.loading.stop();
    } catch (e) {
      this.msg = type;
      this.actionMessage = new ActionAlert(
        true,
        true,
        this.translocoService.translate(
          'settings.account.messages.accountInformationNotUpdated'
        ),
        'error'
      );
      this.loading.stop();
    }
  }

  async updatePassword(type: any): Promise<any> {
    this.loading.start();
    try {
      const formData = this.passwordChangeForm.getRawValue();
      const payload = {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      };
      await this.loggedInUserService.changePassword(payload);
      this.passwordChangeForm.reset();
      this.msg = type;
      this.actionMessage = new ActionAlert(
        true,
        true,
        this.translocoService.translate(
          'settings.account.messages.passwordChanged'
        ),
        'success'
      );
      this.loading.stop();
      this.logout();
    } catch (e: any) {
      this.msg = type;
      this.actionMessage = new ActionAlert(
        true,
        true,
        e.error?.message ||
          this.translocoService.translate(
            'settings.account.messages.passwordNotChanged'
          ),
        'error'
      );
      this.loading.stop();
    }
  }

  async logout(): Promise<any> {
    await this.authService.logout();
  }

  confirmationValidator = (control: FormControl): { [s: string]: boolean } => {
    if (!control.value) {
      return { required: true };
    } else if (
      control.value !== this.passwordChangeForm.controls.newPassword.value
    ) {
      return { confirm: true, error: true };
    }
    return {};
  };

  async getCountries(): Promise<void> {
    this.countries = await this.countryService.getCountries();
  }
}
